import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import * as Yup from "yup";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { usePostLogInMutation } from "../../services/auth";
import { showError, showToast } from "../../constants/toasts";
import {
  getFromStorage,
  removeFromStorage,
  setToStorage,
} from "../../helpers/storage";
import { setCredentials } from "../../reducers/authSlice";
import { useAppDispatch } from "../../hooks/store";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import Loader from "../../constants/Loader";
import useTranslation from "../../hooks/traslation";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const translation = useTranslation();

  const [loginMutation, { isLoading }] = usePostLogInMutation();

  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required(translation.error.emailReq)
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          translation.auth.enterValidEmail
        ),
      password: Yup.string()
        .required(translation.error.passRequired)
        .min(6, translation.error.validPass),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken);
      let body = {
        email: values.email,
        password: values.password,
        deviceToken: fcmToken || "123",
        deviceType: "WEB",
      };

      try {
        const response = await loginMutation(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Logged in successfully.");
          setToStorage(STORAGE_KEYS.token, response?.data?.accessToken || "");
          dispatch(
            setCredentials({
              user: response?.data || null,
              token: response?.data?.accessToken || "",
            })
          );

          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }

          navigate("/dashboard");
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
        console.log(error);
      }
    },
  });
  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthLayout>
      <Loader isLoading={isLoading} />
      <div className="authBox">
        <h2>{translation.auth.login}</h2>
        <form onSubmit={formik.handleSubmit}>
          <div className="control_group">
            <label>{translation.auth.email}</label>
            <TextField
              className="text_field"
              placeholder={translation.auth.enterEmail}
              hiddenLabel
              fullWidth
              name="email"
              id="email"
              inputProps={{ maxLength: 80 }}
              value={formik.values.email}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={formik.touched.email && formik.errors.email}
            />
          </div>
          <div className="control_group">
            <label>{translation.auth.password}</label>
            <TextField
              className="text_field"
              placeholder={translation.auth.password}
              fullWidth
              hiddenLabel
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              value={formik.values.password}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={<Checkbox />}
                name="remember"
                label={translation.auth.remember}
                checked={formik.values.remember}
                onChange={formik.handleChange}
              />
            </div>
            <a
              href="javacript:void(0)"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
            >
              {translation.auth.forgotPassword}?
            </a>
          </div>

          <div className="form_btn">
            <Button className="btn btn_primary w_100" type="submit">
              {translation.auth.login}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
